import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative gradient-hero">
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl">NutriGuide</span>
          </Link>
          <div className="max-w-md">
            <h2 className="font-display text-3xl font-bold text-slate-900">Eat for your health.</h2>
            <p className="mt-3 text-slate-600">Set up your health profile and get personalized food guidance instantly.</p>
          </div>
          <div></div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Create account</h1>
            <p className="mt-2 text-sm text-slate-500">Takes less than a minute.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="rounded-xl h-11" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="rounded-xl h-11" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="rounded-xl h-11" />
            <div className="text-xs text-slate-400">At least 6 characters.</div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-full h-11 bg-emerald-600 hover:bg-emerald-700"
            data-testid="register-submit-btn"
          >
            {busy ? "Creating…" : "Create account"}
          </Button>

          <div className="text-sm text-slate-500 text-center">
            Already have one?{" "}
            <Link to="/login" className="text-emerald-700 font-medium hover:underline" data-testid="register-to-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
